import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component, HostBinding, AfterViewInit, ViewChild, ElementRef, HostListener, ChangeDetectorRef, signal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderStorageService } from 'render/render-storage/render-storage.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { CookieService } from 'render/services/render-cookies.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { combineLatest, filter, switchMap, takeUntil } from 'rxjs';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';

import { SliceDragEventsService } from 'extensions/st/slice-drag/front/services/slice-drag-events.service';
import { SubscribeNewsletterService } from 'extensions/st/slice-drag/front/services/subscribe-newsletter.service';


interface SliceDragHostStyles extends SliceBaseHostStyles {
  justifyContent: string;
  alignItems: string;
  alignContent: string;
}

@Component({
  selector: 'slice-drag-render',
  templateUrl: './slice-drag-render.component.html',
  styleUrls: ['./slice-drag-render.component.scss']
})
export class SliceDragRenderComponent extends SliceBaseRenderComponent implements AfterViewInit {

  @HostBinding('style.justify-content')
  public justifyContent!: string;

  @HostBinding('style.align-items')
  public alignItems!: string;

  @HostBinding('style.align-content')
  public alignContent!: string;

  @ViewChild('image') public image?: ElementRef<HTMLImageElement>;

  public isComponentVisible = signal(false);
  public canBeClosed = signal(false);
  public canBeMove = signal(false);
  public deletedSlices: string[] = [];
  public firstTimeSlices: string[] = [];

  constructor(
    protected readonly domSanitizer: DomSanitizer,
    protected readonly assetsSharedService: AssetsSharedService,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly idtSharedRenderService: IdtSharedRenderService,
    protected readonly renderConfigurationService: RenderConfigurationService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly el: ElementRef<HTMLElement>,
    protected readonly renderStateService: RenderStateService,
    protected readonly renderStorageService: RenderStorageService,
    protected readonly renderSlugService: RenderSlugService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly sliceDragEventsService: SliceDragEventsService,
    private readonly subscribeNewsletterService: SubscribeNewsletterService,
    private readonly cookieService: CookieService
  ) {
    super(
      domSanitizer,
      assetsSharedService,
      breakpointObserver,
      idtSharedRenderService,
      renderConfigurationService,
      cd,
      el,
      renderStateService,
      renderStorageService,
      renderSlugService
    );
  }

  @HostBinding('class')
  public get classes(): string {
    let classes: string = (this.slice?.settings?.styles?.shadow && this.slice.settings.styles.shadow !== 'none') ? `${this.slice.settings.styles?.shadow} ` : '';

    if (this.slice?.settings?.styles?.borderRadiusType) {
      if (this.slice.settings.styles.borderRadiusType === 'straight') {
        classes += 'border-radius-straight';
      } else if (this.slice.settings.styles.borderRadiusType === 'semi-round') {
        classes += 'border-radius-semi-round';
      } else if (this.slice.settings.styles.borderRadiusType === 'round') {
        classes += 'border-radius-round';
      }
    }

    return classes + ((this.slice?.settings?.general.classes as string) ? ` ${this.slice?.settings?.general.classes}` : '');
  }

  @HostBinding('style.cursor')
  public get cursorType(): string {
    return this.hasCustomCursorIcon && !this.slice?.settings?.general?.customCursor?.showPointer ? 'none' : 'inherit';
  }

  @HostBinding('style.flex-direction')
  public get flexDirection(): string {
    return this.slice?.settings?.general?.flexDirection ?? 'column';
  }

  @HostBinding('style.flex-wrap')
  public get flexWrap(): string {
    return this.slice?.settings?.general?.flexWrap ?? 'initial';
  }

  @HostBinding('class.hidden')
  public get display(): boolean {
    return this.slots.filter((slot) => !slot.hidden).length === 0;
  }

  @HostBinding('attr.data-composition-id')
  public get compositionId(): string | undefined {
    const compositionId = this.slice?.settings?.general?.compositionId;
    return compositionId !== '' ? compositionId : undefined;
  }

  @HostBinding('style.opacity')
  public get opacity(): number {
    const opacity = this.slice?.settings?.styles?.opacity;
    return opacity !== '' ? opacity / 100 : 1;
  }

  @HostListener('mouseenter') public onEnter() {
    if (this.image) {
      this.image.nativeElement.style.display = 'block';
      this.image.nativeElement.style.pointerEvents = 'none';
      this.el.nativeElement.addEventListener('pointermove', this.imageFollowMouse);
    }
  }

  @HostListener('mouseleave') public onLeave() {
    if (this.image) {
      this.image.nativeElement.style.display = '';
      this.image.nativeElement.style.pointerEvents = '';
      this.el.nativeElement.removeEventListener('pointermove', this.imageFollowMouse);
    }
  }

  @HostBinding('class.section-position')
  public get positionTypeMobile(): boolean {
    return this.hasPositionEnabled;
  }

  @HostBinding('style.--x-position')
  public get xPosition(): string | undefined {
    if (!this.hasPositionEnabled) {
      return undefined;
    }

    return this.isMobile
      ? this.slice?.settings?.general?.position?.xPositionMobile ?? '0px'
      : this.slice?.settings?.general?.position?.xPositionDesktop ?? '0px';
  }

  @HostBinding('style.--y-position')
  public get yPosition(): string | undefined {
    if (!this.hasPositionEnabled) {
      return undefined;
    }

    return this.isMobile
      ? this.slice?.settings?.general?.position?.yPositionMobile ?? '0px'
      : this.slice?.settings?.general?.position?.yPositionDesktop ?? '0px';
  }

  public get hasCustomCursorIcon(): boolean {
    return !!this.slice?.settings?.general?.customCursor?.showImage
      && !!this.slice?.settings?.general?.customCursor?.image?.image;
  }

  public get customCursorIcon(): string | undefined {
    const iconUrl = this.slice?.settings?.general?.customCursor?.image?.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl, assetsUrl, akamaiOptions);
  }

  public get isDragAndDropEnabled(): boolean {
    return !!this.slice?.settings?.general?.enableDragAndDrop;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public ngAfterViewInit() {
    if (this.slice?.settings?.general?.enableStartDelay) {
      const delaySeconds = this.slice.settings.general.startDelaySeconds || 0;
      setTimeout(() => {
        this.isComponentVisible.set(true);
        this.cd.markForCheck();
      }, delaySeconds * 1000);
    } else {
      this.isComponentVisible.set(true);
    }

    if (this.slice.settings?.general?.callApiCloseSection) {
      const slug = this.renderSlugService.getSlug();
      const accessToken = this.cookieService.getCookie('access_token');

      combineLatest([
        this.renderStateService.getCountry$(slug),
        this.renderStateService.getLanguage$(slug)
      ]).pipe(
        filter(([country, lang]) => !!country && !!lang),
        takeUntil(this.clearSubscriptions$),
        switchMap(([country, lang]) => this.subscribeNewsletterService.search(
          country!,
          lang!,
          accessToken
        )),
        filter(({ newsletter }) => !!newsletter)
      ).subscribe(() => this.deleteSlice(false));
    }

    if (this.slice?.settings?.general.closeSection) {
      this.canBeClosed.set(true);

      if (this.slice?.settings?.general.keepCloseSection && this.renderStorageService.exists('deletedSlices', 'sessionstorage')) {
        const filterDeletedSlices = this.renderStorageService.getItem('deletedSlices', 'sessionstorage')!;
        if (filterDeletedSlices.includes(this.slice.id)) {
          this.deleteSlice(false);
        }
      }
    }

    if (this.slice?.settings?.general.isFirstTimeIndicator || !!this.slice?.settings?.general.firstTimeText) {
      const firstTimeSlices = this.renderStorageService.getItem('firstTimeSlices', 'sessionstorage') ?? '[]';
      const parsedFirstTimeSlices = JSON.parse(firstTimeSlices);

      if (parsedFirstTimeSlices.includes(this.slice.id)) {
        this.canBeMove.set(false);
      } else {
        this.canBeMove.set(true);
        this.firstSlice();
      }
    }

    this.setupCloseSliceDragSubscription();
  }

  public deleteSlice(addToStorage: boolean = true) {
    this.slots = [];
    this.canBeClosed.set(false);
    this.el.nativeElement.remove();
    if (addToStorage && this.slice?.settings?.general.keepCloseSection) {
      if (this.renderStorageService.exists('deletedSlices', 'sessionstorage')) {
        const deletedSlices: string = this.renderStorageService.getItem('deletedSlices', 'sessionstorage')!;
        this.deletedSlices = JSON.parse(deletedSlices);
      }
      this.deletedSlices.push(this.slice.id);
      this.renderStorageService.insert('deletedSlices', JSON.stringify(this.deletedSlices), 'sessionstorage');
    }
  }

  public firstSlice() {
    if (this.renderStorageService.exists('firstTimeSlices', 'sessionstorage')) {
      const firstTimeSlices: string = this.renderStorageService.getItem('firstTimeSlices', 'sessionstorage')!;
      this.firstTimeSlices = JSON.parse(firstTimeSlices);
    }

    this.firstTimeSlices.push(this.slice.id);
    this.renderStorageService.insert('firstTimeSlices', JSON.stringify(this.firstTimeSlices), 'sessionstorage');
  }

  public onRenderComponent(): void {
    setTimeout(() => {
      this.canBeMove.set(false);
    }, 5000);
  }

  protected onSliceInit(): void { /* empty */ }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings.styles.bgColor,
      justifyContent: this.slice.settings.general.justifyContent,
      alignItems: this.slice.settings.general.alignItems,
      alignContent: this.slice.settings.general.alignContent,
      marginTop: this.slice.settings.styles.marginTop,
      marginRight: this.slice.settings.styles.marginRight,
      marginLeft: this.slice.settings.styles.marginLeft,
      marginBottom: this.slice.settings.styles.marginBottom,
      paddingTop: this.slice.settings.styles.paddingTop,
      paddingRight: this.slice.settings.styles.paddingRight,
      paddingLeft: this.slice.settings.styles.paddingLeft,
      paddingBottom: this.slice.settings.styles.paddingBottom
    });
  }

  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings.styles.bgColor,
      justifyContent: this.slice.settings.general.justifyContent,
      alignItems: this.slice.settings.general.alignItems,
      alignContent: this.slice.settings.general.alignContent,
      marginTop: breakpointStyles.marginTop ?? defaultBpSettings.marginTop ?? '',
      marginRight: breakpointStyles.marginRight ?? defaultBpSettings.marginRight ?? '',
      marginLeft: breakpointStyles.marginLeft ?? defaultBpSettings.marginLeft ?? '',
      marginBottom: breakpointStyles.marginBottom ?? defaultBpSettings.marginBottom ?? '',
      paddingTop: breakpointStyles.paddingTop ?? defaultBpSettings.paddingTop ?? '',
      paddingRight: breakpointStyles.paddingRight ?? defaultBpSettings.paddingRight ?? '',
      paddingLeft: breakpointStyles.paddingLeft ?? defaultBpSettings.paddingLeft ?? '',
      paddingBottom: breakpointStyles.paddingBottom ?? defaultBpSettings.paddingBottom ?? ''
    });
  }

  private setHostStyles(styles: SliceDragHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.justifyContent = this.toFlex(styles.justifyContent);
    this.alignItems = this.toFlex(styles.alignItems);
    this.alignContent = this.toFlex(styles.alignContent);
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

  private readonly imageFollowMouse = (event: PointerEvent): void => {
    const pieceDimensions = this.el.nativeElement.getBoundingClientRect();
    const posX = event.clientX - pieceDimensions.x - (this.image!.nativeElement.offsetWidth / 2);
    const posY = event.clientY - pieceDimensions.y - (this.image!.nativeElement.offsetHeight / 2);

    this.image!.nativeElement.style.left = `${posX}px`;
    this.image!.nativeElement.style.top = `${posY}px`;
  };


  private get hasPositionEnabled(): boolean {
    return !!this.slice?.settings?.general?.position?.isPositionEnabled;
  }

  private setupCloseSliceDragSubscription(): void {
    this.sliceDragEventsService.closeSlice$
      .pipe(takeUntil(this.clearSubscriptions$))
      .subscribe((event) => {
        if (this.shouldCloseSlice(event.pieceId)) {
          setTimeout(() => {
            this.deleteSlice(false);
          }, 5000);
        }
      });
  }

  private shouldCloseSlice(pieceId: string): boolean {
    const isCloseSlice = Object.values(this.slice?.children ?? {})
      .flat()
      .includes(pieceId);

    if (isCloseSlice && this.slice?.settings?.general.keepCloseSection) {
      return isCloseSlice;
    }

    if (isCloseSlice && !this.slice?.settings?.general.keepCloseSection) {
      this.sliceDragEventsService.emitCloseSlice({
        pieceId: this.slice?.id ?? ''
      });
    }
    return false;
  }

}
